import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Share2, Copy, ExternalLink, Trash2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

export default function ShareDialog({ projectId, project, onChange }) {
    const [open, setOpen] = useState(false);
    const [enabled, setEnabled] = useState(!!project?.share_enabled);
    const [token, setToken] = useState(project?.share_token || "");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setEnabled(!!project?.share_enabled);
        setToken(project?.share_token || "");
    }, [project?.share_enabled, project?.share_token]);

    const shareUrl = token ? `${window.location.origin}/s/${token}` : "";

    const enable = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/projects/${projectId}/share`);
            setEnabled(true);
            setToken(data.share_token);
            toast.success("Share-link geactiveerd");
            onChange?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const disable = async () => {
        setBusy(true);
        try {
            await api.delete(`/projects/${projectId}/share`);
            setEnabled(false);
            setToken("");
            toast.success("Share-link ingetrokken");
            onChange?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Link gekopieerd");
        } catch {
            toast.error("Kon niet kopiëren");
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="outline"
                    className="rounded-md"
                    data-testid="share-button"
                >
                    <Share2 className="w-4 h-4 mr-2" />
                    Delen
                </Button>
            </DialogTrigger>
            <DialogContent data-testid="share-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display tracking-tight text-2xl">
                        Project delen
                    </DialogTitle>
                </DialogHeader>

                <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-sm font-medium">Publieke share-link</div>
                        <p className="text-xs text-zinc-500 mt-1">
                            Iedereen met de link kan bestanden bekijken, zoeken en downloaden.
                        </p>
                    </div>
                    <Switch
                        checked={enabled}
                        onCheckedChange={(v) => (v ? enable() : disable())}
                        disabled={busy}
                        data-testid="share-toggle"
                    />
                </div>

                {enabled && shareUrl && (
                    <div className="space-y-3">
                        <div className="border border-zinc-200 rounded-md p-3 flex items-center gap-2 bg-white">
                            <LinkIcon className="w-4 h-4 text-zinc-400 shrink-0" />
                            <code
                                className="text-xs font-mono text-zinc-700 truncate flex-1"
                                data-testid="share-url"
                            >
                                {shareUrl}
                            </code>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <Button
                                onClick={copy}
                                className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                                data-testid="share-copy"
                            >
                                <Copy className="w-4 h-4 mr-2" />
                                Kopieer
                            </Button>
                            <a
                                href={shareUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-200 rounded-md text-sm font-medium hover:bg-zinc-50"
                                data-testid="share-open"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Open
                            </a>
                            <Button
                                variant="outline"
                                onClick={disable}
                                className="text-red-600 border-red-200 hover:bg-red-50 ml-auto"
                                data-testid="share-revoke"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Intrekken
                            </Button>
                        </div>
                        <p className="text-xs text-zinc-500">
                            ⚠️ Bezoekers met de link kunnen alle bestanden van dit project zien
                            en downloaden — ook AI-beschrijvingen. Trek de link in zodra je
                            samenwerking afgerond is.
                        </p>
                    </div>
                )}

                {!enabled && (
                    <p className="text-sm text-zinc-500">
                        Schakel delen in om een unieke link te genereren. Je kan deze later
                        terug intrekken — de oude link werkt dan niet meer.
                    </p>
                )}
            </DialogContent>
        </Dialog>
    );
}
